import * as React from "react";
import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";

import { tagLabel, type TagSummary } from "@/lib/tags";
import { cn } from "@/lib/utils";

const BASE =
  "inline-flex items-center gap-1 rounded-full border border-border/70 bg-surface/70 px-2.5 py-0.5 text-xs font-medium tracking-tight text-muted-foreground transition-smooth";

/** A clickable hashtag that opens its dedicated page. */
export function TagChip({
  tag,
  className,
  size = "sm",
}: {
  tag: Pick<TagSummary, "slug" | "name">;
  className?: string;
  size?: "sm" | "md";
}) {
  return (
    <Link
      to="/tag/$slug"
      params={{ slug: tag.slug }}
      className={cn(
        BASE,
        "relative z-10 hover:border-primary/60 hover:bg-accent hover:text-foreground",
        size === "md" && "px-3 py-1 text-sm",
        className,
      )}
    >
      {tagLabel(tag)}
    </Link>
  );
}

/** Non-navigating variant, for editors and moderation views. */
export function TagPill({
  tag,
  onRemove,
  onClick,
  className,
  active = false,
}: {
  tag: Pick<TagSummary, "slug" | "name">;
  onRemove?: (() => void) | undefined;
  onClick?: (() => void) | undefined;
  className?: string | undefined;
  active?: boolean | undefined;
}) {
  const content = (
    <>
      {tagLabel(tag)}
      {onRemove ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          className="rounded-full p-0.5 text-muted-foreground hover:text-foreground"
          aria-label={`Remove ${tagLabel(tag)}`}
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      ) : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(BASE, "hover:border-primary/60 hover:bg-accent hover:text-foreground", active && "border-primary/60 bg-accent text-foreground", className)}
      >
        {content}
      </button>
    );
  }

  return <span className={cn(BASE, active && "border-primary/60 bg-accent text-foreground", className)}>{content}</span>;
}

/**
 * Renders a capped list of hashtags with a "+N more" affordance so cards
 * never get overcrowded.
 */
export function TagList({
  tags,
  max = 3,
  className,
  size = "sm",
}: {
  tags: Pick<TagSummary, "slug" | "name">[];
  max?: number;
  className?: string;
  size?: "sm" | "md";
}) {
  if (tags.length === 0) return null;
  const shown = tags.slice(0, max);
  const remaining = tags.length - shown.length;

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {shown.map((tag) => (
        <TagChip key={tag.slug} tag={tag} size={size} />
      ))}
      {remaining > 0 ? (
        <span className="text-xs text-muted-foreground">+{remaining} more</span>
      ) : null}
    </div>
  );
}
